import { z } from 'zod';

/**
 * A preliminary lead may genuinely have no value yet, so this stays optional and
 * only rejects values that are not a non-negative number.
 */
const estimatedValueSchema = z
    .string()
    .optional()
    .refine((val) => {
        if (!val) return true;
        const num = parseFloat(val);
        return !isNaN(num) && num >= 0;
    }, 'Estimated value must be 0 or greater');

export const createLeadSchema = z.object({
    name: z.string().min(1, 'Object name is required'),
    company_id: z.string().min(1, 'Company is required'),
    person_id: z.string().optional(),
    lead_source: z.string().optional(),
    priority: z.string().min(1, 'Priority is required'),
    estimated_value: estimatedValueSchema,
    offer_due_date: z.string().optional(),
    lead_owner_id: z.string().optional(),
    notes: z.string().optional(),
});

export const createLeadWithOwnerSchema = z.object({
    name: z.string().min(1, 'Object name is required'),
    company_id: z.string().min(1, 'Company is required'),
    person_id: z.string().optional(),
    lead_source: z.string().optional(),
    priority: z.string().min(1, 'Priority is required'),
    estimated_value: estimatedValueSchema,
    offer_due_date: z.string().optional(),
    lead_owner_id: z.string().min(1, 'Lead owner is required'),
    notes: z.string().optional(),
});

export type CreateLeadFormData = z.infer<typeof createLeadSchema>;

export const editLeadSchema = z.object({
    // The object name follows the lead into its calculations and its project, so an
    // edit must not be able to blank it either.
    name: z.string().min(1, 'Object name is required'),
    // A lead always belongs to a company — the column is NOT NULL and the create
    // form has always demanded one, so editing must not be able to clear it.
    company_id: z.string().min(1, 'Company is required'),
    person_id: z.string().optional(),
    country: z.string().optional(),
    estimated_value: estimatedValueSchema,
    offer_due_date: z.string().optional(),
    notes: z.string().optional(),
    lead_owner_id: z.string().optional(),
    address: z.string().optional(),
    address_lat: z.number().nullable().optional(),
    address_lng: z.number().nullable().optional(),
    address_street: z.string().nullable().optional(),
    address_house_number: z.string().nullable().optional(),
    address_postal_code: z.string().nullable().optional(),
    address_city: z.string().nullable().optional(),
    address_municipality: z.string().nullable().optional(),
    address_country: z.string().nullable().optional(),
});

export type EditLeadFormData = z.infer<typeof editLeadSchema>;
